import React, { useEffect, useState, useCallback, useRef } from 'react';
import BackDeskLayout from '../../components/layout/BackDeskLayout';
import { api } from '../../services/api';
import MIcon from '../../components/common/MIcon';
import { useTenant } from '../../context/TenantContext';
import { Button } from '../../components/ui';


const TAB_SECTIONS = 'sections';
const TAB_PHONES   = 'phones';
const TAB_ASTERISK = 'asterisk';

/* ─────────────────── helpers UI ─────────────────── */
const pill = (text, green) => (
  <span style={{
    fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:10,
    background: green ? '#F0FDF4' : '#F3F4F6',
    color:      green ? '#15803D' : '#9CA3AF',
    border:     `1px solid ${green ? '#86EFAC' : '#E5E7EB'}`,
  }}>{text}</span>
);

function Modal({ title, onClose, children, footer }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.35)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#fff', borderRadius:12, width:460, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 8px 40px rgba(0,0,0,.15)', overflow:'hidden' }}>
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <span style={{ fontSize:14, fontWeight:700, color:'#111827' }}>{title}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'#9CA3AF', lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:'18px 20px', overflowY:'auto', flex:1 }}>{children}</div>
        {footer && (
          <div style={{ padding:'12px 20px', borderTop:'1px solid #F3F4F6', display:'flex', justifyContent:'flex-end', gap:8, flexShrink:0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ fontSize:11, fontWeight:600, color:'#6B7280', textTransform:'uppercase', letterSpacing:.4, display:'block', marginBottom:4 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width:'100%', padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:7,
  fontSize:13, boxSizing:'border-box', outline:'none',
};

/* ─────────────────── modal sección ─────────────────── */
function SectionModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState({ name: initial?.name || '', sort_order: initial?.sort_order ?? 0 });
  const [err,  setErr]  = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('El nombre es obligatorio'); return; }
    setSaving(true); setErr('');
    try { await onSave(form); onClose(); }
    catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <Modal title={initial ? 'Editar sección' : 'Nueva sección'} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : initial ? 'Guardar' : 'Crear'}
        </Button>
      </>}>
      <Field label="Nombre *">
        <input style={inputStyle} value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="Ej: Rectorado"/>
      </Field>
      <Field label="Orden">
        <input style={inputStyle} type="number" value={form.sort_order} onChange={e => setForm(f=>({...f,sort_order:Number(e.target.value)}))}/>
      </Field>
      {err && <div style={{ fontSize:12, color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:6, padding:'6px 10px' }}>{err}</div>}
    </Modal>
  );
}

/* ─────────────────── modal contacto ─────────────────── */
function ContactModal({ initial, sectionId, onSave, onClose }) {
  const [form, setForm] = useState({
    name:       initial?.name      || '',
    extension:  initial?.extension || '',
    role:       initial?.role      || '',
    sort_order: initial?.sort_order ?? 0,
  });
  const [err,  setErr]  = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name.trim() || !form.extension.trim()) { setErr('Nombre y extensión son obligatorios'); return; }
    setSaving(true); setErr('');
    try { await onSave(form); onClose(); }
    catch (e) { setErr(e.message); setSaving(false); }
  };

  return (
    <Modal title={initial ? 'Editar contacto' : 'Nuevo contacto'} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : initial ? 'Guardar' : 'Añadir'}
        </Button>
      </>}>
      <Field label="Nombre *">
        <input style={inputStyle} value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="Ej: Rubén Calderón"/>
      </Field>
      <Field label="Extensión *">
        <input style={inputStyle} value={form.extension} onChange={e => setForm(f=>({...f,extension:e.target.value}))} placeholder="Ej: 5014"/>
      </Field>
      <Field label="Cargo / Rol (opcional)">
        <input style={inputStyle} value={form.role} onChange={e => setForm(f=>({...f,role:e.target.value}))} placeholder="Ej: Rector"/>
      </Field>
      <Field label="Orden">
        <input style={inputStyle} type="number" value={form.sort_order} onChange={e => setForm(f=>({...f,sort_order:Number(e.target.value)}))}/>
      </Field>
      {err && <div style={{ fontSize:12, color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:6, padding:'6px 10px' }}>{err}</div>}
    </Modal>
  );
}

/* ─────────────────── pestaña Secciones ─────────────────── */
function TabSections({ sections, onReload, ACCENT='#0085CA', ACCENT_BG='#EBF6FC', BORDER='#D1E9F5' }) {
  const [expanded,      setExpanded]      = useState({});
  const [sectionModal,  setSectionModal]  = useState(null);
  const [contactModal,  setContactModal]  = useState(null);
  const [localSections, setLocalSections] = useState(sections);
  const [search,        setSearch]        = useState('');
  const dragSectionIdx  = useRef(null);
  const dragContactRef  = useRef(null); // {sectionId, idx}
  const [draggingSectionIdx, setDraggingSectionIdx] = useState(null);
  const [draggingContact,    setDraggingContact]    = useState(null);
  const isDragging  = useRef(false);
  const lastDragEnd = useRef(0);

  // Sync local state when sections prop changes
  useEffect(() => { setLocalSections(sections); }, [sections]);

  const toggle = id => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const handleSaveSection = async (form) => {
    if (sectionModal === 'new') await api.createExtSection(form);
    else                        await api.updateExtSection(sectionModal.id, form);
    onReload();
  };

  const handleDeleteSection = async (s) => {
    if (!confirm(`¿Eliminar la sección "${s.name}" y todos sus contactos?`)) return;
    await api.deleteExtSection(s.id);
    onReload();
  };

  const handleToggleSection = async (s) => {
    await api.updateExtSection(s.id, { active: s.active ? 0 : 1 });
    onReload();
  };

  const handleSaveContact = async (form) => {
    if (contactModal.contact) await api.updateExtContact(contactModal.contact.id, form);
    else                      await api.createExtContact(contactModal.sectionId, form);
    onReload();
  };

  const handleDeleteContact = async (c) => {
    if (!confirm(`¿Eliminar el contacto "${c.name}"?`)) return;
    await api.deleteExtContact(c.id);
    onReload();
  };

  // ── Drag & drop secciones ──────────────────────────────
  const onSectionDragStart = (e, idx) => {
    dragSectionIdx.current = idx;
    isDragging.current = true;
    setDraggingSectionIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onSectionDragOver = (e, idx) => {
    e.preventDefault();
    if (dragSectionIdx.current === idx) return;
    const reordered = [...localSections];
    const [moved] = reordered.splice(dragSectionIdx.current, 1);
    reordered.splice(idx, 0, moved);
    dragSectionIdx.current = idx;
    setLocalSections(reordered);
  };
  const onSectionDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onSectionDragEnd = async () => {
    lastDragEnd.current = Date.now();
    isDragging.current = false;
    setDraggingSectionIdx(null);
    if (dragSectionIdx.current === null) return;
    const order = localSections.map((s, i) => ({ id: s.id, sort_order: i }));
    await api.reorderExtSections(order);
    dragSectionIdx.current = null;
    onReload();
  };

  // ── Drag & drop contactos ──────────────────────────────
  const onContactDragStart = (e, sectionId, idx) => {
    dragContactRef.current = { sectionId, idx };
    setDraggingContact({ sectionId, idx });
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
  };
  const onContactDragOver = (e, sectionId, idx) => {
    e.preventDefault(); e.stopPropagation();
    const ref = dragContactRef.current;
    if (!ref || ref.sectionId !== sectionId || ref.idx === idx) return;
    setLocalSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      const contacts = [...s.contacts];
      const [moved] = contacts.splice(ref.idx, 1);
      contacts.splice(idx, 0, moved);
      dragContactRef.current = { sectionId, idx };
      return { ...s, contacts };
    }));
  };
  const onContactDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onContactDragEnd = async (sectionId) => {
    setDraggingContact(null);
    const section = localSections.find(s => s.id === sectionId);
    if (!section || !dragContactRef.current) return;
    const order = section.contacts.map((c, i) => ({ id: c.id, sort_order: i }));
    await api.reorderExtContacts(order);
    dragContactRef.current = null;
    onReload();
  };

  return (
    <>
      {sectionModal !== null && (
        <SectionModal
          initial={sectionModal === 'new' ? null : sectionModal}
          onSave={handleSaveSection}
          onClose={() => setSectionModal(null)}
        />
      )}
      {contactModal !== null && (
        <ContactModal
          initial={contactModal.contact || null}
          sectionId={contactModal.sectionId}
          onSave={handleSaveContact}
          onClose={() => setContactModal(null)}
        />
      )}

      <div style={{ marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Buscar sección o contacto…"
          style={{ flex:1, height:34, padding:'0 12px', border:'1px solid #E5E7EB', borderRadius:7, fontSize:13, outline:'none', background:'#fff' }}
        />
        <Button variant="primary" onClick={() => setSectionModal('new')} style={{flexShrink:0}}>+ Nueva sección</Button>
      </div>

      {sections.length === 0 ? (
        <div style={{ padding:'48px 0', textAlign:'center', color:'#9CA3AF' }}>
          <MIcon name="folder_open" size={36} color="#D1D5DB" style={{ marginBottom:8 }}/>
          <div style={{ fontSize:14, marginTop:8 }}>No hay secciones</div>
          <div style={{ fontSize:12, marginTop:4 }}>Crea una sección para empezar a añadir extensiones</div>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {localSections.filter(s => !search ||
            s.name.toLowerCase().includes(search.toLowerCase()) ||
            s.contacts?.some(ct => ct.name.toLowerCase().includes(search.toLowerCase()) || ct.extension.includes(search))
          ).map((s, sIdx) => (
            <div key={s.id}
              onDragOver={e => onSectionDragOver(e, sIdx)}
              onDrop={onSectionDrop}
              onDragEnd={onSectionDragEnd}
              style={{ background:'#fff', border: draggingSectionIdx===sIdx ? '1.5px solid #0085CA' : '1px solid #E5E7EB', borderRadius:10, overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,.04)', opacity: draggingSectionIdx!==null && draggingSectionIdx!==sIdx ? 0.6 : 1, transition:'opacity .15s, border .15s' }}>
              {/* cabecera sección — drag icon fuera del área clickable */}
              <div style={{ display:'flex', alignItems:'center', background: expanded[s.id] ? ACCENT_BG : '#fff', borderBottom: expanded[s.id] ? `1px solid ${BORDER}` : 'none' }}>
                {/* Handle drag — no forma parte del área de toggle */}
                <span
                  draggable
                  onDragStart={e => onSectionDragStart(e, sIdx)}
                  style={{display:'flex',alignItems:'center',cursor: draggingSectionIdx!==null ? 'grabbing' : 'grab',flexShrink:0,padding:'10px 2px 10px 14px',userSelect:'none'}}
                  onClick={e=>e.stopPropagation()}>
                  <MIcon name="drag_indicator" size={18} style={{ color: draggingSectionIdx===sIdx ? '#0085CA' : '#D1D5DB' }}/>
                </span>
                {/* Área clickable */}
                <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, padding:'10px 14px 10px 4px', cursor:'pointer' }}
                  onClick={() => toggle(s.id)}>
                <MIcon name={expanded[s.id] ? 'expand_less' : 'expand_more'} size={18} style={{ color:'#6B7280' }}/>
                <span style={{ flex:1, fontSize:13, fontWeight:700, color: expanded[s.id] ? ACCENT : '#111827' }}>{s.name}</span>
                <span style={{ fontSize:11, color:'#9CA3AF', marginRight:8 }}>{s.contacts?.length || 0} contactos</span>
                {pill(s.active ? 'Activa' : 'Inactiva', s.active)}
                </div>
                <div style={{ display:'flex', gap:4, paddingRight:14, flexShrink:0 }} onClick={e => e.stopPropagation()}>
                  <Button variant="secondary" size="sm" onClick={() => setContactModal({ sectionId: s.id })}>
                    + Contacto
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setSectionModal(s)}>
                    Editar
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => handleToggleSection(s)}>
                    {s.active ? 'Ocultar' : 'Mostrar'}
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDeleteSection(s)}>
                    Eliminar
                  </Button>
                </div>
              </div>

              {/* lista contactos */}
              {expanded[s.id] && (
                <div>
                  {(!s.contacts || s.contacts.length === 0) ? (
                    <div style={{ padding:'16px', fontSize:12, color:'#9CA3AF', textAlign:'center' }}>
                      Sin contactos — pulsa "+ Contacto" para añadir
                    </div>
                  ) : s.contacts.map((c, i) => (
                    <div key={c.id}
                      onDragOver={e => onContactDragOver(e, s.id, i)}
                      onDrop={onContactDrop}
                      onDragEnd={() => onContactDragEnd(s.id)}
                      style={{ display:'flex', alignItems:'center', padding:'8px 14px', borderBottom: i < s.contacts.length - 1 ? '1px solid #F9FAFB' : 'none', gap:10, opacity: draggingContact?.sectionId===s.id && draggingContact?.idx!==i ? 0.5 : 1 }}>
                      <span
                        draggable
                        onDragStart={e => onContactDragStart(e, s.id, i)}
                        style={{display:'flex',alignItems:'center',cursor:'grab',flexShrink:0,userSelect:'none'}}
                        onClick={e=>e.stopPropagation()}>
                        <MIcon name="drag_indicator" size={16} style={{ color:'#D1D5DB' }}/>
                      </span>
                      <div style={{ flex:1, minWidth:0 }}>
                        {c.role && <div style={{ fontSize:11, color:'#6B7280' }}>{c.role}</div>}
                        <div style={{ fontSize:13, fontWeight:c.role ? 600 : 400, color: c.active ? '#111827' : '#9CA3AF' }}>{c.name}</div>
                      </div>
                      <span style={{ fontFamily:'monospace', fontSize:13, fontWeight:700, color: ACCENT, background: ACCENT_BG, border:`1px solid ${BORDER}`, borderRadius:6, padding:'1px 8px' }}>
                        {c.extension}
                      </span>
                      {!c.active && pill('Oculto', false)}
                      <div style={{ display:'flex', gap:4 }}>
                        <Button variant="secondary" size="sm" onClick={() => setContactModal({ sectionId: s.id, contact: c })}>
                          Editar
                        </Button>
                        <Button variant="danger" size="sm" onClick={() => handleDeleteContact(c)}>
                          ✕
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/* ─────────────────── sugeridor de extensión libre ─────────────────── */
function FreeExtensionFinder({ accent: ACCENT = '#0085CA', accentBg: ACCENT_BG = '#EBF6FC', border: BORDER = '#D1E9F5' }) {
  const [suggestions, setSuggestions] = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [copied,      setCopied]      = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getExtFree(8);
      setSuggestions(data.suggestions || []);
    } catch { setSuggestions([]); }
    finally { setLoading(false); }
  };

  const copy = (ext) => {
    navigator.clipboard.writeText(ext).catch(() => {});
    setCopied(ext);
    setTimeout(() => setCopied(false), 1800);
  };

  const next = suggestions?.[0];

  return (
    <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden', marginBottom:20 }}>
      <div style={{ padding:'12px 16px', background:'#F8FAFF', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <MIcon name="phone_forwarded" size={17} style={{ color: ACCENT }}/>
          <span style={{ fontSize:13, fontWeight:700, color:'#374151' }}>Asignar extensión libre (rango 5xxx)</span>
        </div>
        <Button variant="secondary" size="sm" onClick={load} disabled={loading} style={{ display:'flex', alignItems:'center', gap:5 }}>
          <MIcon name="refresh" size={14} style={{ color:'#6B7280' }}/>
          {loading ? 'Consultando…' : suggestions === null ? 'Consultar' : 'Actualizar'}
        </Button>
      </div>

      {suggestions === null ? (
        <div style={{ padding:'20px 16px', fontSize:12, color:'#9CA3AF', textAlign:'center' }}>
          Pulsa "Consultar" para encontrar extensiones libres
        </div>
      ) : suggestions.length === 0 ? (
        <div style={{ padding:'20px 16px', fontSize:12, color:'#9CA3AF', textAlign:'center' }}>
          No se encontraron extensiones libres en el rango 5000–5999
        </div>
      ) : (
        <div style={{ padding:'14px 16px' }}>
          {/* Extensión sugerida principal */}
          <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:14, padding:'12px 16px', background: ACCENT_BG, borderRadius:8, border:`1px solid ${BORDER}` }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, color:'#6B7280', fontWeight:600, textTransform:'uppercase', letterSpacing:.4, marginBottom:3 }}>Próxima libre</div>
              <div style={{ fontSize:28, fontWeight:800, fontFamily:'monospace', color: ACCENT, letterSpacing:2 }}>{next}</div>
            </div>
            <button onClick={() => copy(next)} style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 14px', borderRadius:7, border:`1px solid ${BORDER}`, background:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, color: copied === next ? '#15803D' : ACCENT, transition:'color .2s' }}>
              <MIcon name={copied === next ? 'check' : 'content_copy'} size={15}/>
              {copied === next ? 'Copiado' : 'Copiar'}
            </button>
          </div>

          {/* Siguientes libres */}
          {suggestions.length > 1 && (
            <div>
              <div style={{ fontSize:11, color:'#9CA3AF', fontWeight:600, textTransform:'uppercase', letterSpacing:.4, marginBottom:8 }}>
                Siguientes libres
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {suggestions.slice(1).map(ext => (
                  <button key={ext} onClick={() => copy(ext)} title="Copiar" style={{ fontFamily:'monospace', fontWeight:700, fontSize:13, color: copied === ext ? '#15803D' : ACCENT, background: copied === ext ? '#F0FDF4' : ACCENT_BG, border:`1px solid ${copied === ext ? '#86EFAC' : BORDER}`, borderRadius:6, padding:'3px 10px', cursor:'pointer', transition:'all .2s' }}>
                    {copied === ext ? '✓ ' : ''}{ext}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────── pestaña Asterisk ─────────────────── */
function TabAsterisk({ blocked, pdiPreview, onReload, ACCENT='#0085CA', ACCENT_BG='#EBF6FC', BORDER='#D1E9F5' }) {
  const [form,   setForm]   = useState({ extension:'', reason:'' });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const handleAdd = async () => {
    if (!form.extension.trim()) { setErr('La extensión es obligatoria'); return; }
    setSaving(true); setErr('');
    try {
      await api.addExtBlocked(form);
      setForm({ extension:'', reason:'' });
      onReload();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleRemove = async (id) => {
    await api.removeExtBlocked(id);
    onReload();
  };

  return (
    <div>
      <FreeExtensionFinder accent={ACCENT} accentBg={ACCENT_BG} border={BORDER}/>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, alignItems:'start' }}>
      {/* Bloqueadas */}
      <div>
        <h3 style={{ margin:'0 0 12px', fontSize:13, fontWeight:700, color:'#374151' }}>Extensiones bloqueadas</h3>
        <p style={{ margin:'0 0 14px', fontSize:12, color:'#6B7280' }}>
          Estas extensiones se excluyen del apartado Todas las extensiones aunque existan en Asterisk.
        </p>
        <div style={{ display:'flex', gap:8, marginBottom:12 }}>
          <input style={{ ...inputStyle, flex:1 }} placeholder="Nº extensión" value={form.extension}
            onChange={e => setForm(f=>({...f,extension:e.target.value}))}/>
          <input style={{ ...inputStyle, flex:2 }} placeholder="Motivo (opcional)" value={form.reason}
            onChange={e => setForm(f=>({...f,reason:e.target.value}))}/>
          <Button variant="primary" onClick={handleAdd} disabled={saving} style={{ flexShrink:0 }}>
            {saving ? '…' : 'Añadir'}
          </Button>
        </div>
        {err && <div style={{ fontSize:12, color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:6, padding:'6px 10px', marginBottom:10 }}>{err}</div>}

        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden' }}>
          {blocked.length === 0 ? (
            <div style={{ padding:'24px', textAlign:'center', fontSize:12, color:'#9CA3AF' }}>Sin extensiones bloqueadas</div>
          ) : blocked.map((b, i) => (
            <div key={b.id} style={{ display:'flex', alignItems:'center', padding:'8px 12px', borderBottom: i < blocked.length-1 ? '1px solid #F3F4F6' : 'none', gap:10 }}>
              <span style={{ fontFamily:'monospace', fontWeight:700, fontSize:13, color:'#B91C1C', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:5, padding:'1px 7px' }}>{b.extension}</span>
              <span style={{ flex:1, fontSize:12, color:'#6B7280' }}>{b.reason || '—'}</span>
              <Button variant="danger" size="sm" onClick={() => handleRemove(b.id)}>
                ✕
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Vista previa PDI */}
      <div>
        <h3 style={{ margin:'0 0 12px', fontSize:13, fontWeight:700, color:'#374151' }}>
          Vista previa Todas las extensiones
          {pdiPreview && <span style={{ fontSize:11, fontWeight:400, color:'#6B7280', marginLeft:6 }}>({pdiPreview.length} extensiones)</span>}
        </h3>
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden', maxHeight:480, overflowY:'auto' }}>
          {pdiPreview === null ? (
            <div style={{ padding:'24px', textAlign:'center', fontSize:12, color:'#9CA3AF' }}>
              No se pudo conectar con Asterisk
            </div>
          ) : pdiPreview.length === 0 ? (
            <div style={{ padding:'24px', textAlign:'center', fontSize:12, color:'#9CA3AF' }}>Sin datos</div>
          ) : pdiPreview.map((p, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 12px', borderBottom: i < pdiPreview.length-1 ? '1px solid #F9FAFB' : 'none', gap:8 }}>
              <span style={{ fontSize:12, color:'#374151', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</span>
              <span style={{ fontFamily:'monospace', fontSize:12, fontWeight:700, color:ACCENT, background:ACCENT_BG, border:`1px solid ${BORDER}`, borderRadius:5, padding:'1px 6px' }}>{p.extension}</span>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}


/* ─────────────────── pestaña Estado Teléfonos ─────────────────── */
function TabPhones({ phones, onReload, ACCENT='#0085CA' }) {
  const [search, setSearch] = useState('');

  const filtered = phones.filter(p =>
    !search ||
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.extension.includes(search) ||
    (p.ip || '').includes(search) ||
    (p.model || '').toLowerCase().includes(search.toLowerCase())
  );

  const online  = phones.filter(p => p.online).length;
  const offline = phones.length - online;

  function fmtDate(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('es-ES', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' });
  }

  return (
    <div>
      <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ padding:'10px 18px', borderRadius:8, background:'#F0FDF4', border:'1px solid #86EFAC', fontSize:13, fontWeight:600, color:'#15803D' }}>
          ● {online} conectados
        </div>
        <div style={{ padding:'10px 18px', borderRadius:8, background:'#F9FAFB', border:'1px solid #E5E7EB', fontSize:13, fontWeight:600, color:'#9CA3AF' }}>
          ○ {offline} desconectados
        </div>
        <div style={{ flex:1 }}/>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Buscar nombre, extensión, IP o modelo…"
          style={{ width:280, height:34, padding:'0 12px', border:'1px solid #E5E7EB', borderRadius:7, fontSize:13, outline:'none' }}
        />
        <Button variant="ghost" onClick={onReload} style={{ height:34, fontSize:12, display:'flex', alignItems:'center', gap:4 }}>
          <MIcon name="refresh" size={14}/> Actualizar
        </Button>
      </div>

      <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'#FAFAFA', borderBottom:'1px solid #F3F4F6' }}>
              {['Estado','Ext.','Nombre','IP','Modelo','Follow Me','Call Waiting','Último registro'].map(h => (
                <th key={h} style={{ padding:'9px 14px', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:.5, color:'#9CA3AF', textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ padding:'32px', textAlign:'center', color:'#9CA3AF', fontSize:13 }}>Sin resultados</td></tr>
            ) : filtered.map((p, i) => (
              <tr key={i} style={{ borderBottom:'1px solid #F9FAFB' }}>
                <td style={{ padding:'10px 14px' }}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:10,
                    background: p.online ? '#F0FDF4' : '#F9FAFB', color: p.online ? '#15803D' : '#9CA3AF',
                    border: `1px solid ${p.online ? '#86EFAC' : '#E5E7EB'}` }}>
                    <span style={{ width:6, height:6, borderRadius:'50%', background: p.online ? '#22C55E' : '#D1D5DB', flexShrink:0 }}/>
                    {p.online ? `${p.lastms}ms` : 'Offline'}
                  </span>
                </td>
                <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:13, fontWeight:700, color:'#0085CA' }}>{p.extension}</td>
                <td style={{ padding:'10px 14px', fontSize:13, color:'#111827', fontWeight:500 }}>{p.name}</td>
                <td style={{ padding:'10px 14px', fontFamily:'monospace', fontSize:12, color:'#6B7280' }}>{p.ip || '—'}</td>
                <td style={{ padding:'10px 14px', fontSize:12, color:'#6B7280', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.model || '—'}</td>
                <td style={{ padding:'10px 14px', textAlign:'center' }}>
                  <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:10,
                    background: p.follow_me ? '#EDE9FE' : '#F9FAFB', color: p.follow_me ? '#7C3AED' : '#9CA3AF',
                    border: `1px solid ${p.follow_me ? '#C4B5FD' : '#E5E7EB'}` }}>
                    {p.follow_me ? 'Activo' : '—'}
                  </span>
                </td>
                <td style={{ padding:'10px 14px', textAlign:'center' }}>
                  <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:10,
                    background: p.call_waiting ? '#F0FDF4' : '#F9FAFB', color: p.call_waiting ? '#15803D' : '#9CA3AF',
                    border: `1px solid ${p.call_waiting ? '#86EFAC' : '#E5E7EB'}` }}>
                    {p.call_waiting ? 'Sí' : '—'}
                  </span>
                </td>
                <td style={{ padding:'10px 14px', fontSize:11, color:'#9CA3AF', whiteSpace:'nowrap' }}>{fmtDate(p.registered_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────── componente principal ─────────────────── */
export default function ExtensionsManager() {
  const tenantCtx = useTenant() || {};
  const { primaryColor = '#0085CA' } = tenantCtx;
  const modules = tenantCtx.modules; // null o undefined = aún cargando, array = cargado
  const ACCENT    = primaryColor;
  const ACCENT_BG = primaryColor + '18';
  const BORDER    = primaryColor + '40';
  // Asterisk visible si no hay módulos configurados (legacy) o si está activo
  // Busca la última entrada del módulo asterisk (la más específica gana)
  const asteriskMod = Array.isArray(modules) ? [...modules].reverse().find(m => m.module === 'asterisk') : undefined;
  // null/undefined = cargando o legacy = mostrar
  // array con asterisk:0 = ocultar
  // array sin entrada asterisk = mostrar (legacy sin config)
  const hasAsterisk = !Array.isArray(modules) || !asteriskMod || Number(asteriskMod.active) !== 0;
  const [tab,        setTab]        = useState(TAB_SECTIONS);
  const [sections,   setSections]   = useState([]);
  const [phones,     setPhones]     = useState([]);
  const [blocked,    setBlocked]    = useState([]);
  const [pdiPreview, setPdiPreview] = useState(null);
  const [loading,    setLoading]    = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sec, pub, bl, ph] = await Promise.all([
        api.getExtSections(),
        api.getExtensions(),
        api.getExtBlocked().catch(() => []),
        api.getExtPhones().catch(() => []),
      ]);
      setSections(sec);
      setPdiPreview(pub.pdi ?? null);
      setBlocked(bl);
      setPhones(ph);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tabBtn = (id, label, icon) => (
    <button onClick={() => setTab(id)} style={{
      display:'flex', alignItems:'center', gap:6,
      padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
      background: tab === id ? ACCENT_BG : 'none',
      color:      tab === id ? ACCENT    : '#6B7280',
      transition:'all .15s',
    }}>
      <MIcon name={icon} size={16}/> {label}
    </button>
  );

  return (
    <BackDeskLayout title="Directorio de Extensiones"
>

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:'1px solid #E5E7EB', paddingBottom:4 }}>
        {tabBtn(TAB_SECTIONS, 'Secciones y contactos', 'contacts')}
        {tabBtn(TAB_ASTERISK, 'Todas las extensiones — Asterisk', 'phone')}
        {tabBtn(TAB_PHONES, 'Estado teléfonos', 'phone_in_talk')}
      </div>

      {loading ? (
        <div className="bd-loading"><div className="bd-spinner-sm"/>Cargando…</div>
      ) : (
        <>
          {tab === TAB_SECTIONS && <TabSections sections={sections} onReload={load} ACCENT={ACCENT} ACCENT_BG={ACCENT_BG} BORDER={BORDER}/>}
          {tab === TAB_ASTERISK && <TabAsterisk blocked={blocked} pdiPreview={pdiPreview} onReload={load} ACCENT={ACCENT} ACCENT_BG={ACCENT_BG} BORDER={BORDER}/>}
          {tab === TAB_PHONES   && <TabPhones phones={phones} onReload={load} ACCENT={ACCENT}/>}
        </>
      )}
    </BackDeskLayout>
  );
}
